// The game's keys and names (front-server src/App/Types/*). Order is the game's own order everywhere it matters.

export const TROOP_KEYS = ["t_sf", "t_sw", "t_a", "t_sc", "t_lc", "t_hc", "t_r", "t_c", "t_n"] as const;
export type TroopKey = (typeof TROOP_KEYS)[number];
export const TROOP_NAMES: Record<TroopKey, string> = {
  t_sf: "Lancer", t_sw: "Sentry", t_a: "Berserker", t_sc: "Scout", t_lc: "Knight", t_hc: "Guardian", t_r: "Ram", t_c: "Ballista", t_n: "Scholar",
};
export const TROOP_PLURAL: Record<TroopKey, string> = {
  t_sf: "Lancers", t_sw: "Sentries", t_a: "Berserkers", t_sc: "Scouts", t_lc: "Knights", t_hc: "Guardians", t_r: "Rams", t_c: "Ballistas", t_n: "Scholars",
};
/** Which building recruits each troop (TroopType::$parentBuilding). */
export const TROOP_BUILDING: Record<TroopKey, BuildingKey> = {
  t_sf: "b_b", t_sw: "b_b", t_a: "b_b", t_sc: "b_st", t_lc: "b_st", t_hc: "b_st", t_r: "b_wo", t_c: "b_wo", t_n: "b_a",
};
/** The research (Forge) key that unlocks each troop (TroopType::$requiredTech). */
export const TROOP_TECH: Record<TroopKey, TechKey> = {
  t_sf: "rd_sf", t_sw: "rd_sw", t_a: "rd_a", t_sc: "rd_sc", t_lc: "rd_lc", t_hc: "rd_hc", t_r: "rd_r", t_c: "rd_c", t_n: "rd_n",
};

export const TECH_KEYS = ["rd_sf", "rd_sw", "rd_a", "rd_sc", "rd_lc", "rd_hc", "rd_r", "rd_c"] as const;
export type TechKey = (typeof TECH_KEYS)[number] | "rd_n";

export const BUILDING_KEYS = ["b_hq", "b_r", "b_b", "b_st", "b_wo", "b_w", "b_c", "b_i", "b_wh", "b_f", "b_s", "b_a", "b_m", "b_wa", "b_h"] as const;
export type BuildingKey = (typeof BUILDING_KEYS)[number];
export const BUILDING_NAMES: Record<BuildingKey, string> = {
  b_hq: "City Hall", b_r: "Rally Point", b_b: "Barracks", b_st: "Stables", b_wo: "Workshop", b_w: "Lumber Mill", b_c: "Quarry",
  b_i: "Iron Mine", b_wh: "Warehouse", b_f: "Farm", b_s: "Forge", b_a: "Academy", b_m: "Market", b_wa: "Walls", b_h: "Hiding Place",
};
/** The City Hall's grouping on the redesign (ARMY · ECONOMY · STORAGE · DEFENSE). */
export const BUILDING_GROUPS: Record<"army" | "economy" | "storage" | "defense", BuildingKey[]> = {
  army: ["b_r", "b_b", "b_st", "b_wo", "b_s", "b_a"],
  economy: ["b_hq", "b_w", "b_c", "b_i", "b_f", "b_m"],
  storage: ["b_wh", "b_h"],
  defense: ["b_wa"],
};

export const RESOURCE_KEYS = ["r_w", "r_c", "r_i"] as const;
export type ResourceKey = (typeof RESOURCE_KEYS)[number];
export const RESOURCE_NAMES: Record<ResourceKey, string> = { r_w: "Wood", r_c: "Clay", r_i: "Iron" };

/** Movements in queue_rally (MovementType::$keys — returning support is emitted as "rp", not the unused "rs" constant). */
export const MOVEMENT_NAMES: Record<string, string> = { a: "Attack", s: "Support", ra: "Returning (attack)", rp: "Returning (support)", rs: "Returning (support)" };
/** Merchant movements in queue_market (MerchantMovementType). */
export const MERCHANT_MOVEMENT_NAMES: Record<string, string> = { s: "Sending", t: "Trade", rs: "Returning", rt: "Returning" };

/** The City Hall's demolish gate (VillageService: can_demolish = City Hall ≥ 15). */
export const DEMOLISH_MIN_HQ = 15;

/** Each building's level bands → its looks I–IV on the city view (the tiers sheet, verified Oct 7; the City View handoff
 *  keeps them, Oct 12 2026 — game/cityScene.ts). */
const BANDS_30 = [7, 15, 23];
const BANDS_25 = [6, 12, 19];
const BANDS_20 = [5, 10, 15];
const BANDS_15 = [4, 8, 12];
export const TIER_BANDS: Partial<Record<BuildingKey, number[]>> = {
  b_hq: BANDS_30, b_w: BANDS_30, b_c: BANDS_30, b_i: BANDS_30, b_f: BANDS_30, b_wh: BANDS_30,
  b_b: BANDS_25, b_m: BANDS_25, b_wa: BANDS_25,
  b_st: BANDS_20, b_s: BANDS_20, b_wo: BANDS_20,
  b_h: BANDS_15,
};
/** 1–4 for tiered buildings; 0 for level 0 (an empty plot); 1 for one-look buildings. */
export function tierOf(key: BuildingKey, level: number): 0 | 1 | 2 | 3 | 4 {
  if (level <= 0) return 0;
  const bands = TIER_BANDS[key];
  if (!bands) return 1;
  if (level <= bands[0]) return 1;
  if (level <= bands[1]) return 2;
  if (level <= bands[2]) return 3;
  return 4;
}
export const TIER_NUMERALS = ["", "I", "II", "III", "IV"] as const;

/** Troop type for the recruit screens' corner label. */
export const TROOP_CLASS: Record<TroopKey, "Infantry" | "Cavalry" | "Siege" | "Scholar"> = {
  t_sf: "Infantry", t_sw: "Infantry", t_a: "Infantry", t_sc: "Cavalry", t_lc: "Cavalry", t_hc: "Cavalry", t_r: "Siege", t_c: "Siege", t_n: "Scholar",
};
/** Art slugs (public/art/unit/icon-<slug>.webp, face-<slug>-<tier>.webp, art-<slug>-<tier>.webp). */
export const TROOP_SLUG: Record<TroopKey, string> = {
  t_sf: "lancer", t_sw: "sentry", t_a: "berserker", t_sc: "scout", t_lc: "knight", t_hc: "guardian", t_r: "ram", t_c: "ballista", t_n: "scholar",
};
/** One-line descriptions for the building heroes (the mockups' copy). */
export const BUILDING_BLURB: Record<BuildingKey, string> = {
  b_hq: "The seat of the city. Each level speeds up every building here.",
  b_r: "Muster your troops here: send attacks and support, and watch what is coming.",
  b_b: "Trains the infantry: Lancers, Sentries and Berserkers.",
  b_st: "Trains the cavalry: Scouts, Knights and Guardians.",
  b_wo: "Builds the siege engines: Rams and Ballistas.",
  b_w: "Lumberjacks fell timber for wood, the resource every building needs.",
  b_c: "Quarrymen cut stone and clay for walls and workshops.",
  b_i: "Miners dig iron for weapons, armour and engines.",
  b_wh: "Stores your wood, clay and iron. Anything produced past capacity is lost.",
  b_f: "Feeds your workers and troops. Every building and soldier needs room on the farm.",
  b_s: "Research your units. Ten points to share; each unit takes up to three.",
  b_a: "Scholars lower a target city's loyalty with every attack — the first step to conquest.",
  b_m: "Merchants carry resources to other cities and trade on the open market.",
  b_wa: "Walls add flat defence and a bonus to every defender inside.",
  b_h: "Hides part of your stock from raiders — the amount grows with its level.",
};
/** Report kinds (ReportType). */
export const REPORT_KINDS: Record<string, string> = {
  re_a: "Attack", re_d: "Defense", re_s: "Support", re_t: "Trade", re_gi: "Guild invite", re_gu: "Guild", re_is: "Spy", re_i: "Incoming",
};

/** The world-map cell's 1–4 size level → the painted keep tier. */
export const KEEP_TIERS = ["Hamlet", "Town", "Fortress", "Citadel"] as const;
